import React, { useState, useEffect } from 'react';
import { FaInfoCircle, FaArrowLeft, FaPaperclip, FaCheckCircle, FaSave, FaTrash, FaFileAlt } from 'react-icons/fa';
import collegeConfig from '../../config/collegeConfig';

const AdditionalInfo = ({ onNavigate }) => {
  const token = localStorage.getItem('token');
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [attachments, setAttachments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [savedMessage, setSavedMessage] = useState({});

  useEffect(() => {
    fetchFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFields = async () => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/additional-info/student`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setFields(data.fields || []);
        const initialValues = {};
        const initialAttachments = {};
        (data.fields || []).forEach(f => {
          initialValues[f._id] = f.submission?.value || '';
          initialAttachments[f._id] = f.submission?.attachment || null;
        });
        setValues(initialValues);
        setAttachments(initialAttachments);
      } else {
        setError(data.message || 'Failed to load information');
      }
    } catch (err) {
      console.error('Error fetching additional info:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (fieldId, file) => {
    if (!file) return;
    setUploadingId(fieldId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setAttachments(prev => ({ ...prev, [fieldId]: data.attachment }));
      } else {
        setError(data.message || 'File upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('File upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveAttachment = async (fieldId) => {
    const attachment = attachments[fieldId];
    if (!attachment) return;
    try {
      await fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ key: attachment.key })
      });
    } catch (err) {
      console.error('Error removing attachment:', err);
    } finally {
      setAttachments(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleViewAttachment = async (attachment) => {
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/upload/signed-url?key=${encodeURIComponent(attachment.key)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok && data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Error viewing attachment:', err);
    }
  };

  const handleSave = async (field) => {
    const value = values[field._id] || '';
    if (field.required && !value.trim()) {
      setError('This field is required');
      return;
    }
    setSavingId(field._id);
    setError('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/additional-info/student/${field._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          value,
          attachment: attachments[field._id] || null
        })
      });
      const data = await res.json();
      if (res.ok) {
        setSavedMessage(prev => ({ ...prev, [field._id]: true }));
        setTimeout(() => {
          setSavedMessage(prev => ({ ...prev, [field._id]: false }));
        }, 2500);
      } else {
        setError(data.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving field:', err);
      setError('Error connecting to server');
    } finally {
      setSavingId(null);
    }
  };

  const renderInput = (field) => {
    const value = values[field._id] || '';
    const commonProps = {
      value,
      onChange: (e) => setValues(prev => ({ ...prev, [field._id]: e.target.value })),
      style: inputStyle
    };

    if (field.fieldType === 'textarea') {
      return <textarea rows="3" placeholder="Type your answer..." {...commonProps} />;
    }
    if (field.fieldType === 'number') {
      return <input type="number" placeholder="Enter number" {...commonProps} />;
    }
    if (field.fieldType === 'date') {
      return <input type="date" {...commonProps} />;
    }
    return <input type="text" placeholder="Type your answer..." {...commonProps} />;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px',
      paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.98)',
        borderRadius: '16px',
        padding: '20px 30px',
        marginBottom: '30px',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)'
      }}>
        <button
          onClick={() => onNavigate('dashboard')}
          style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '12px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          <FaArrowLeft />
          Back to Dashboard
        </button>
        <div>
          <h1 style={{ margin: '0 0 5px', fontSize: '24px', color: '#333' }}>
            Additional Information
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
            Fill in details requested by your department
          </p>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fdecea',
          color: '#c62828',
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '20px',
          fontWeight: 500
        }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.98)',
          borderRadius: '16px',
          padding: '60px',
          textAlign: 'center',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ color: '#666' }}>Loading...</p>
        </div>
      )}

      {!loading && fields.length === 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.98)',
          borderRadius: '16px',
          padding: '60px',
          textAlign: 'center',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <FaInfoCircle style={{ fontSize: '40px', color: '#bbb', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: '#333' }}>Nothing to fill right now</h3>
          <p style={{ margin: 0, color: '#666' }}>
            Your department hasn't requested any additional information yet.
          </p>
        </div>
      )}

      {!loading && fields.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {fields.map(field => (
            <div key={field._id} style={{
              background: 'rgba(255, 255, 255, 0.98)',
              borderRadius: '16px',
              padding: '24px 28px',
              boxShadow: '0 5px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ margin: '0 0 4px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {field.title}
                {field.required && <span style={{ color: '#e74c3c', fontSize: '14px' }}>*</span>}
              </h3>
              {field.description && (
                <p style={{ margin: '0 0 14px', color: '#777', fontSize: '13px' }}>{field.description}</p>
              )}

              {renderInput(field)}

              {field.allowUpload && (
                <div style={{ marginTop: '14px' }}>
                  {attachments[field._id] ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      background: '#f4f6fb',
                      padding: '10px 14px',
                      borderRadius: '10px'
                    }}>
                      <FaFileAlt style={{ color: '#667eea' }} />
                      <span
                        style={{ flex: 1, fontSize: '13px', color: '#333', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => handleViewAttachment(attachments[field._id])}
                      >
                        {attachments[field._id].name}
                      </span>
                      <button
                        onClick={() => handleRemoveAttachment(field._id)}
                        style={{ border: 'none', background: 'none', color: '#c62828', cursor: 'pointer' }}
                        title="Remove file"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ) : (
                    <label style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 16px',
                      background: '#f4f6fb',
                      color: '#444',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}>
                      <FaPaperclip />
                      {uploadingId === field._id ? 'Uploading...' : 'Attach file (optional)'}
                      <input
                        type="file"
                        hidden
                        disabled={uploadingId === field._id}
                        onChange={(e) => handleFileChange(field._id, e.target.files[0])}
                      />
                    </label>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '16px' }}>
                <button
                  onClick={() => handleSave(field)}
                  disabled={savingId === field._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '13px'
                  }}
                >
                  <FaSave /> {savingId === field._id ? 'Saving...' : 'Save'}
                </button>
                {savedMessage[field._id] && (
                  <span style={{ color: '#2c8e6e', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                    <FaCheckCircle /> Saved
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid #ddd',
  borderRadius: '10px',
  fontSize: '14px',
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

export default AdditionalInfo;
